import {
	AlertDialog,
	AlertDialogTrigger,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
	AlertDialogAction,
} from '@/common/ui/alert-dialog'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { Label } from '@/common/ui/label'
import { Calendar } from '@/common/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { CalendarIcon, Clock } from 'lucide-react'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { cn } from '@/common/utils'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/common/ui/form'
import { useEffect, useMemo } from 'react'

interface ForceCompleteDepositModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	id: string
	amount: number
	forceComplete: any // mutation object
}

export default function ForceCompleteDepositModal({
	open,
	setOpen,
	id,
	amount,
	forceComplete,
}: ForceCompleteDepositModalProps) {
	const depositSchema = useMemo(
		() =>
			z.object({
				amount: z
					.number()
					.min(amount, 'จำนวนเงินต้องมากกว่าหรือเท่ากับ ' + amount),
				slipVerifyAt: z.date({
					required_error: 'กรุณาเลือกวันที่และเวลา',
				}),
				note: z.string().optional(),
			}),
		[amount],
	)

	type DepositFormValues = z.infer<typeof depositSchema>

	const form = useForm<DepositFormValues>({
		resolver: zodResolver(depositSchema),
	})

	const handleForceComplete = (values: DepositFormValues) => {
		forceComplete.mutate(
			{
				id,
				amount: values.amount,
				slipVerifyAt: values.slipVerifyAt.toISOString(),
				note: values.note ? values.note : undefined,
			},
			{
				onSuccess: () => {
					setOpen(false)
				},
			},
		)
	}

	useEffect(() => {
		form.setValue('amount', amount)
		form.setValue('slipVerifyAt', new Date())

		return () => {
			form.reset()
		}
	}, [form, amount])

	return (
		<AlertDialog open={open}>
			<AlertDialogTrigger asChild onClick={() => setOpen(true)}>
				<Button>สำเร็จรายการ</Button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>ยืนยันการทำรายการ</AlertDialogTitle>
					<AlertDialogDescription>
						คุณต้องการทำรายการฝากเงินนี้ให้สำเร็จใช่หรือไม่?
					</AlertDialogDescription>
				</AlertDialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(handleForceComplete)}>
						<div className="grid gap-4 py-4">
							<div className="grid grid-cols-4 items-center gap-4">
								<Label className="text-right">จำนวนเงิน</Label>
								<div className="col-span-3">
									<FormField
										control={form.control}
										name="amount"
										render={({ field }) => (
											<FormItem>
												<FormControl>
													<Input
														type="number"
														{...field}
														value={+field.value}
														onChange={(e) => {
															field.onChange(Number(e.target.value))
														}}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>
							<div className="grid grid-cols-4 items-center gap-4">
								<Label className="text-right">วันที่และเวลาตรวจสอบสลิป</Label>
								<div className="col-span-3">
									<FormField
										control={form.control}
										name="slipVerifyAt"
										render={({ field }) => (
											<FormItem>
												<FormControl>
													<Popover>
														<PopoverTrigger asChild>
															<Button
																variant="outline"
																className={cn(
																	'w-full justify-start text-left font-normal',
																	!field.value && 'text-muted-foreground',
																)}
															>
																<CalendarIcon className="mr-2 h-4 w-4" />
																{field.value ? (
																	format(field.value, 'PPP HH:mm น.', {
																		locale: th,
																	})
																) : (
																	<span>เลือกวันที่และเวลา</span>
																)}
															</Button>
														</PopoverTrigger>
														<PopoverContent
															className="w-auto p-0"
															align="start"
														>
															<Calendar
																mode="single"
																selected={field.value}
																onSelect={field.onChange}
																initialFocus
															/>
															<div className="border-t border-border p-3">
																<div className="flex items-center gap-2">
																	<Clock className="h-4 w-4" />
																	<Input
																		type="time"
																		value={format(field.value, 'HH:mm')}
																		onChange={(e) => {
																			const [hours, minutes] =
																				e.target.value.split(':')
																			const newDate = new Date(field.value)
																			newDate.setHours(parseInt(hours))
																			newDate.setMinutes(parseInt(minutes))
																			field.onChange(newDate)
																		}}
																		className="w-full"
																	/>
																</div>
															</div>
														</PopoverContent>
													</Popover>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>
							<div className="grid grid-cols-4 items-center gap-4">
								<Label className="text-right">หมายเหตุ (ถ้ามี)</Label>
								<div className="col-span-3">
									<FormField
										control={form.control}
										name="note"
										render={({ field }) => (
											<FormItem>
												<FormControl>
													<Textarea
														{...field}
														placeholder="ระบุเหตุผลหรือหมายเหตุเพิ่มเติม..."
														className="min-h-[80px]"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>
						</div>
						<AlertDialogFooter>
							<AlertDialogCancel type="button" onClick={() => setOpen(false)}>
								ยกเลิก
							</AlertDialogCancel>
							<AlertDialogAction
								disabled={forceComplete.isPending}
								type="submit"
							>
								ยืนยัน
							</AlertDialogAction>
						</AlertDialogFooter>
					</form>
				</Form>
			</AlertDialogContent>
		</AlertDialog>
	)
}
